/* eslint-disable @typescript-eslint/no-explicit-any */
import ImageOptimize from '@/components/ImageOptimize'
import { ProductStockStatus } from '@/constants'
import { labelPositionMapStyle } from '@/constants/positions'
import { useNotification } from '@/containers'
import { useLightbox } from '@/hooks/useLightBox'
import { IconDescription, IconRating, IconSpec } from '@/styles/Icon'
import type { Product, ProductDetail } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import type { FC } from 'react'
import { useRef } from 'react'
import type { Settings } from 'react-slick'
import Slider from 'react-slick'
const breakpoints = [4320, 2160, 1080, 640, 384, 256, 128]

interface Props {
  images: string[]
  alt: string
  label: ProductDetail
  refs: any
  product: Product
  handleClickRef: (ref: any) => void
}
interface CustomArrowProps {
  className?: string
  style?: React.CSSProperties
  onClick?: () => void
}

const CustomPrevArrow: React.FC<CustomArrowProps> = ({
  className,
  onClick,
}) => {
  return (
    <div className={`slickPrevCS ${className}`} onClick={onClick}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='7'
        height='14'
        viewBox='0 0 7 14'
        fill='#000000'
      >
        <g opacity='1'>
          <path
            d='M5.231 13.64C5.40074 13.844 5.64455 13.9721 5.90879 13.9963C6.17303 14.0205 6.43605 13.9387 6.64 13.769C6.84396 13.5993 6.97213 13.3555 6.99632 13.0912C7.02051 12.827 6.93874 12.564 6.76901 12.36L2.30001 7L6.76901 1.64001C6.85305 1.53902 6.91638 1.42247 6.95539 1.297C6.99439 1.17154 7.0083 1.03963 6.99632 0.908788C6.98434 0.77795 6.94671 0.650753 6.88557 0.534459C6.82444 0.418165 6.74099 0.315051 6.64001 0.231005C6.43605 0.0612659 6.17303 -0.0205003 5.90879 0.00369158C5.64455 0.0278834 5.40074 0.156052 5.23101 0.360004L0.231005 6.36C0.0813386 6.53969 -0.000619256 6.76615 -0.000619276 7C-0.000619297 7.23386 0.0813385 7.46032 0.231005 7.64L5.231 13.64Z'
            fill='#000000'
          />
        </g>
      </svg>
    </div>
  )
}

const CustomNextArrow: React.FC<CustomArrowProps> = ({
  className,

  onClick,
}) => {
  return (
    <div className={`slickNextCS ${className}`} onClick={onClick}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='#000000'
      >
        <g opacity='1'>
          <path
            d='M6.769 1.35999C6.59926 1.15604 6.35545 1.02787 6.09121 1.00368C5.82697 0.979489 5.56395 1.06126 5.36 1.23099C5.15604 1.40073 5.02787 1.64454 5.00368 1.90878C4.97949 2.17302 5.06126 2.43604 5.23099 2.63999L9.7 7.99999L5.23099 13.36C5.14695 13.461 5.08362 13.5775 5.04461 13.703C5.00561 13.8285 4.9917 13.9604 5.00368 14.0912C5.01566 14.222 5.05329 14.3492 5.11443 14.4655C5.17557 14.5818 5.25901 14.6849 5.36 14.769C5.56395 14.9387 5.82697 15.0205 6.09121 14.9963C6.35545 14.9721 6.59926 14.8439 6.769 14.64L11.769 8.64C11.9187 8.46031 12.0006 8.23385 12.0006 7.99999C12.0006 7.76614 11.9187 7.53968 11.769 7.35999L6.769 1.35999Z'
            fill='#000000'
          />
        </g>
      </svg>
    </div>
  )
}
export const Gallery: FC<Props> = ({
  images,
  alt,
  label,
  refs,
  handleClickRef,
  product,
}: Props) => {
  const { systemConfig } = useNotification()
  const { openLightBox, renderLightBox } = useLightbox()
  const sliderMain = useRef<Slider | null>(null)

  const settingsMain: Settings = {
    slidesToScroll: 1,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    className: 'productGalleryMain',
  }
  const percent = Math.ceil(100 - (product?.finalPrice / product?.price) * 100)
  return (
    <div className='image-summary'>
      {images && (
        <>
          <div className='image-summary-image relative'>
            <div className='relative px-5 xl:px-10'>
              {renderLightBox({
                slides: images.map((e, i) => {
                  const width = 600 * 4
                  const height = 600 * 4
                  return {
                    src:
                      i === 0 && product.videoUrl && product.videoThumbnail
                        ? product.videoThumbnail
                        : e,
                    type: i === 0 && product.videoUrl ? undefined : 'image',
                    width,
                    videoUrl: product?.videoUrl,
                    height,
                    srcSet: breakpoints.map(breakpoint => {
                      const breakpointHeight = Math.round(
                        (height / width) * breakpoint
                      )
                      return {
                        src:
                          i === 0 && product.videoUrl && product.videoThumbnail
                            ? product.videoThumbnail
                            : e,
                        width: breakpoint,
                        height: breakpointHeight,
                      }
                    }),
                  }
                }),
              })}

              <div className='relative'>
                {percent && percent > 0 ? (
                  <span className='absolute left-12 top-3 z-[99] flex h-11 w-20 items-center justify-center rounded-lg bg-primary-second text-2xl font-bold text-white'>
                    {'-' + percent + '%'}
                  </span>
                ) : (
                  ''
                )}
                {label.stockStatus !== ProductStockStatus.OUT_OF_STOCK &&
                  label?.labels.map((item, index) => {
                    return (
                      <>
                        {item?.image ? (
                          <span
                            className={`${labelPositionMapStyle.get(
                              item?.position
                            )} label hiddenScrollBar absolute block`}
                            style={{ zIndex: 20 + index }}
                            key={index}
                          >
                            <ImageOptimize
                              src={item?.image}
                              alt={getTranslate(item?.name)}
                              width={217}
                              height={40}
                              className='h-auto w-auto'
                            />
                          </span>
                        ) : (
                          ''
                        )}
                      </>
                    )
                  })}
                {images.length === 0 && product.videoUrl ? (
                  <ImageOptimize
                    className='cartImage w-full object-cover'
                    src={
                      systemConfig?.imagePlaceholder ||
                      process.env.NEXT_PUBLIC_IMAGE_PLACEHOLDER ||
                      ''
                    }
                    loading='lazy'
                    alt={alt}
                    width={512}
                    height={512}
                  />
                ) : (
                  <Slider {...settingsMain} ref={sliderMain}>
                    {images.map((e, i) => {
                      return (
                        <div
                          className='relative flex justify-center px-14 lg:px-16 xl:px-[70px]'
                          key={i}
                        >
                          <ImageOptimize
                            onClick={() => openLightBox(i)}
                            className='relative mx-auto aspect-square w-full cursor-pointer object-contain'
                            height={512}
                            width={512}
                            alt={alt}
                            src={
                              i === 0 &&
                              product.videoUrl &&
                              product.videoThumbnail
                                ? product.videoThumbnail
                                : e
                            }
                            priority={true}
                          />
                          {i === 0 && product.videoUrl && (
                            <svg
                              className='absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer'
                              width={70}
                              onClick={() => openLightBox(i)}
                              height={70}
                              viewBox='0 0 70 70'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M35 0C15.67 0 0 15.67 0 35C0 54.33 15.67 70 35 70C54.33 70 70 54.33 70 35C69.9795 15.6787 54.3215 0.0206543 35 0Z'
                                fill='#F1005D'
                              />

                              <polygon
                                points='28,22 28,48 48,35'
                                fill='#FFFFFF'
                              />
                            </svg>
                          )}
                        </div>
                      )
                    })}
                  </Slider>
                )}
              </div>
            </div>

            <div
              className={`linkSections relative left-[50%] z-[50] mt-[-40px] flex w-full translate-x-[-50%] flex-wrap items-center justify-center gap-2 text-dark-800`}
            >
              <div
                className={`item text-xs`}
                onClick={() => handleClickRef(refs.specRef)}
              >
                <div
                  className={`relative flex h-[80px] w-[80px] cursor-pointer flex-wrap justify-center rounded-lg bg-white px-1 py-2 text-center shadow-base`}
                  data-name={'#spec'}
                >
                  <IconSpec />

                  <span className='mt-1 block h-8 leading-4'>
                    Thông số kỹ thuật
                  </span>
                </div>
              </div>
              <div
                className={`item text-xs`}
                onClick={() => handleClickRef(refs.descRef)}
              >
                <div
                  className={`relative flex h-[80px] w-[80px] cursor-pointer flex-wrap justify-center rounded-lg bg-white px-1 py-2 text-center shadow-base`}
                  data-name={'#description'}
                >
                  <IconDescription />
                  <span className='mt-1 block h-8 leading-4'>
                    Thông tin sản phẩm
                  </span>
                </div>
              </div>

              <div
                className={`item text-xs`}
                onClick={() => handleClickRef(refs.reviewRef)}
              >
                <div
                  className={`relative flex h-[80px] w-[80px] cursor-pointer flex-wrap justify-center rounded-lg bg-white px-1 py-2 text-center shadow-base`}
                  data-name={'#review'}
                >
                  <IconRating />
                  <span className='mt-1 block h-8 leading-4'>Đánh giá</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
